import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon } from '../icons/AppIcon';
import { InsetModal as Modal } from '../InsetModal';
import { NODE_TYPE_META } from './flow-types';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { colors, spacing, typography, withAlpha } from '../../theme';
import { designTokens } from '../../theme/design-system';

/**
 * "Continue from…": the steps of a run's flow version, to move the run to one of them. The run
 * then goes on from that step right away in its conversation, with the values it has saved.
 */
export function FlowRunStepPicker({ visible, nodes, currentNodeId, busy, onClose, onPick }: {
  visible: boolean;
  nodes: Array<{ id: string; name?: string; type?: string }>;
  currentNodeId?: string;
  busy?: boolean;
  onClose: () => void;
  onPick: (nodeId: string) => void;
}) {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [search, setSearch] = useState('');
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return nodes.filter((n) => !q || `${n.name || ''} ${n.type || ''}`.toLowerCase().includes(q));
  }, [nodes, search]);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('flowRuns.move.title')}</Text>
            <TouchableOpacity onPress={onClose} accessibilityLabel={t('common.close')}>
              <Ionicons name="close" size={20} color={colors.text.secondary as string} />
            </TouchableOpacity>
          </View>
          <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('flowRuns.move.hint')}</Text>
          {nodes.length > 8 && (
            <TextInput
              style={[styles.search, { textAlign: rtl.textStart }]}
              value={search}
              onChangeText={setSearch}
              placeholder={t('flowRuns.move.search')}
              placeholderTextColor={colors.text.secondary as string}
            />
          )}
          <ScrollView style={styles.list}>
            {shown.map((node) => {
              const meta = NODE_TYPE_META.find((m) => m.type === node.type);
              const current = node.id === currentNodeId;
              return (
                <TouchableOpacity key={node.id} disabled={busy} style={[styles.row, { flexDirection: rtl.rowDirection }, current && styles.rowCurrent]} onPress={() => onPick(node.id)}>
                  <View style={[styles.icon, { backgroundColor: withAlpha(meta?.color || (colors.primary as string), 0.12) }]}>
                    <AppIcon name={(meta?.icon || 'ellipse-outline')} size={16} color={meta?.color || (colors.primary as string)} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.name, { textAlign: rtl.textStart }]} numberOfLines={1}>{node.name || meta?.label || node.id}</Text>
                    <Text style={[styles.type, { textAlign: rtl.textStart }]} numberOfLines={1}>{meta?.label || node.type}{current ? ` · ${t('flowRuns.move.current')}` : ''}</Text>
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const getStyles = () => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', alignItems: 'center', justifyContent: 'center', padding: spacing.md },
  sheet: { width: '100%', maxWidth: 440, maxHeight: '80%', backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, padding: spacing.md, gap: spacing.sm },
  header: { alignItems: 'center', justifyContent: 'space-between' },
  title: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.text.primary },
  hint: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  search: { minHeight: 36, paddingHorizontal: spacing.sm, borderRadius: designTokens.borderRadius.sm, borderWidth: 1, borderColor: colors.border, color: colors.text.primary, backgroundColor: colors.background.base },
  list: { flexGrow: 0 },
  row: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs, paddingHorizontal: spacing.xs, borderRadius: designTokens.borderRadius.sm },
  rowCurrent: { backgroundColor: withAlpha(colors.primary as string, 0.08) },
  icon: { width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center' },
  name: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.medium, color: colors.text.primary },
  type: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
});
